import { Component, TemplateRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { animate, state, style, transition, trigger, AnimationEvent } from '@angular/animations';

import type { SystemNotificationModel } from '../../../../Admin/SystemNotifications/models/system-notification.model';
import { SystemNotificationsTypeEnum } from '../../../../Admin/SystemNotifications/enums/system-notifications-type.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { SystemNotificationsStyleStateEnum } from '../../../../Admin/SystemNotifications/enums/system-notifications-style-state.enum';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';

export interface MessageIconModel {
    normal?: string;
    outline?: string;
}

export enum MessageIconTypesEnum {
    normal,
    outline,
}
@Component({
    selector: 'agpr-message',
    templateUrl: './message.component.html',
    styleUrls: ['./message.component.scss'],
    animations: [
        trigger('notificationContent', [
            state('hidden', style({
                height: '0',
                opacity: 0
            })),
            state('visible', style({
                height: '*',
                opacity: 1
            })),
            transition('visible <=> hidden', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ],
    standalone: false
})
export class MessageComponent implements OnInit {

    @Input() id: string;
    @Input() closeable: boolean = false;
    @Input() toggleable: boolean = false;
    @Input() toggleableContent: TemplateRef<any>;
    @Input() collapsed: boolean = false;
    @Input() isSystemNotification: boolean = false;
    @Input() systemNotification: SystemNotificationModel;
    @Input() icon: string = null;
    @Input() loading: boolean = false;
    @Input() bottomMargin: boolean = true;
    @Input() iconStyle: MessageIconTypesEnum = MessageIconTypesEnum.outline;
    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.default;
    @Input() centerContent: boolean = false;
    @Input() noBottomCornerRadius: boolean = false;
    @Input() autoFit: boolean = false;
    @Input() useWhiteBg: boolean = false;
    @Input() toggleTooltip: string;
    @Input() tooltipPosition: string;
    @Input() bannerIcon: boolean = false;
    @Input() showBannerMessage: boolean = false;
    @Input() set severity(severity: messageSeverityEnum | SystemNotificationsTypeEnum) {
        this.type = severity;
        switch (severity) {
            case messageSeverityEnum.info:
            case SystemNotificationsTypeEnum.info:
                this.messageClass = 'agpr-message--info';
                this.messageIcon = {
                    outline: 'fa-regular fa-circle-info',
                    normal: 'fa-regular fa-circle-info'
                };
                break;
            case messageSeverityEnum.success:
                this.messageClass = 'agpr-message--success';
                this.messageIcon = { outline: 'agpr-i-line-success' };
                break;
            case messageSeverityEnum.warning:
            case SystemNotificationsTypeEnum.important:
                this.messageClass = 'agpr-message--warning';
                this.messageIcon = {
                    outline: 'fa-regular fa-circle-exclamation',
                    normal: 'fa-regular fa-circle-exclamation'
                };
                break;
            case messageSeverityEnum.error:
            case SystemNotificationsTypeEnum.critical:
                this.messageClass = 'agpr-message--error';
                this.messageIcon = {
                    outline: 'fa-regular fa-minus-circle',
                    normal: 'fa-regular fa-minus-circle'
                };
                break;
            case messageSeverityEnum.noContent:
                this.messageClass = 'agpr-message--noContent';
                this.messageIcon = { outline: 'fa-duotone fa-regular fa-file-xmark' };
                break;
            case messageSeverityEnum.noContentDark:
                this.messageClass = 'agpr-message--noContentDark';
                this.messageIcon = { outline: 'fa-regular fa-duotone fa-list-ul' };
                break;
            case messageSeverityEnum.noContentPale:
                this.messageClass = 'agpr-message--noContentPale';
                this.messageIcon = { outline: 'fa-regular fa-duotone fa-list-ul' };
                break;
            case messageSeverityEnum.noStaff:
                this.messageClass = 'agpr-message--noStaff';
                this.messageIcon = { outline: 'fa-regular fa-duotone fa-duotone fa-user-slash' };
                break;
            case SystemNotificationsTypeEnum.maintenance:
                this.messageClass = 'agpr-message--maintenance';
                this.messageIcon = {
                    normal: 'fa-regular fa-wrench-simple',
                    outline: 'fa-regular fa-wrench-simple'
                };
                break;
            case messageSeverityEnum.noContentLarge:
                this.messageClass = 'agpr-message--noContentLarge';
                this.messageIcon = {
                    normal: 'fa-duotone fa-light fa-file-xmark',
                    outline: 'fa-duotone fa-light fa-file-xmark'
                };
                break;
        }
    }

    @Output() collapsedChange = new EventEmitter();
    @Output() beforeToggle = new EventEmitter();
    @Output() afterToggle = new EventEmitter();
    @Output() dismiss = new EventEmitter<any>();
    @Output() updateNotification = new EventEmitter();

    messageClass: string;
    messageIcon: MessageIconModel;
    type: messageSeverityEnum | SystemNotificationsTypeEnum = null;

    animating: boolean;
    open: boolean = true;

    readonly iconTypes = MessageIconTypesEnum;
    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly includeContent = {
        'info' : true,
        'success' : true,
        'warning' : true,
        'error' : true,
        'noContent' : false,
        'noContentDark' : false,
        'noContentPale' : false,
        'noContentLarge' : false
    };

    constructor() {}

    ngOnInit() {
        if (this.systemNotification?.title && this.systemNotification?.message) {
            this.toggleable = true;
            this.closeable = true;
        }
        if (this.toggleable && this.systemNotification?.styleState === SystemNotificationsStyleStateEnum.collapsed) {
            this.collapsed = true;
        }
    }

    /**
     * Toggle animation for system notifications
     */
    toggle(event) {
        if (this.animating) {
            return false;
        }
        this.animating = true;
        this.beforeToggle.emit({originalEvent: event, collapsed: this.collapsed});

        if (this.toggleable) {
            if (this.collapsed) {
                this.expand(event);
            } else {
                this.collapse(event);
            }
        }

        event.preventDefault();
    }

    expand(event) {
        this.collapsed = false;
        if (this.isSystemNotification) {
            this.systemNotification.styleState = SystemNotificationsStyleStateEnum.default;
            this.updateNotification.emit(this.systemNotification);
        }
        this.collapsedChange.emit(this.collapsed);
    }

    collapse(event) {
        this.collapsed = true;
        if (this.isSystemNotification) {
            this.systemNotification.styleState = SystemNotificationsStyleStateEnum.collapsed;
            this.updateNotification.emit(this.systemNotification);
        }
        this.collapsedChange.emit(this.collapsed);
    }

    onToggleDone(event: AnimationEvent) {
        this.animating = false;
        this.afterToggle.emit({originalEvent: event, collapsed: this.collapsed});
    }

    dismissMessage(event: any) {
        this.dismiss.emit(event);
        this.open = false;
    }
}
